import React, { FC, useRef } from 'react';
import BodyText from '@fenderdigital/ui/BodyText';
import Flex from '@fenderdigital/ui/Flex';
import Spacer from '@fenderdigital/ui/Spacer';
import Link from '../../../Link';
import LevelIndicator from '../../../LevelIndicator';
import { isElementVisible } from '../../../../utils/dom-utils';
import * as S from './ResultItem-styled';

interface ResultItemProps {
  result: any;
  rowId: string;
  selectedOptionId: string | null | undefined;
  type: string | undefined;
  onClick: (e: any) => void;
  instrument?: string;
}

const ResultItem: FC<ResultItemProps> = ({
  result,
  rowId,
  selectedOptionId,
  onClick,
  type,
  instrument = 'guitar',
}) => {
  const itemRef = useRef<HTMLLIElement>(null);
  const isSelected = selectedOptionId === rowId;

  const composeURL = (): string => {
    if (type === 'Collections') return `/${instrument}/collections/${result.slug}`;
    if (type === 'Artists') return result?.url;
    return `/${result.objectType === 'lesson' ? 'lesson' : 'course'}/${result.slug}`;
  };

  if (isSelected) {
    // Scroll selected item into view if navigating by keyboard
    if (itemRef.current && !isElementVisible(itemRef.current)) {
      itemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  return (
    <S.Row
      key={result.slug}
      role="option"
      id={rowId}
      aria-selected={isSelected}
      isSelected={isSelected}
      ref={itemRef}
    >
      <Link to={composeURL()} onClick={onClick} tabIndex="-1" trackEvent={result?.trackEvent}>
        <Flex as="article">
          <S.Image src={result.image} alt="" type={type} />
          <Spacer spacing={type === 'Collections' ? 'md1' : 'md3'} direction="vertical" />
          <Flex direction="column" justify="center">
            <BodyText
              fontFamily="futuraDemi"
              css={`
                line-height: 1;
              `}
            >
              {result.title}
            </BodyText>
            <BodyText textSize="f2" textColor="midGray">
              {result?.artists?.[0]?.name}
            </BodyText>
            {type === 'Skills' && (
              <BodyText textSize="f2" textColor="midGray" textTransform="capitalize">
                {result.type}
              </BodyText>
            )}
            {(type === 'Songs' || type === 'Riffs') && (
              <LevelIndicator currentLevel={result.level} completeColor="black" />
            )}
            {type === 'Collections' && (
              <BodyText textSize="f2" textColor="midGray">
                {!!result.stats.skills && `${result.stats.skills} Skills`}
                {!!result.stats.skills && !!result.stats.songs && ` • `}
                {!!result.stats.songs && `${result.stats.songs} Songs`}
              </BodyText>
            )}
          </Flex>
        </Flex>
      </Link>
    </S.Row>
  );
};

export default ResultItem;
